import { memo, useEffect, useState } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { InputComponent } from '../../../components/inputComponents/InputComponent';
import { SelectOption } from '../../../components/selectComponents/SelectOption';
import { AllocateDates } from '../../../components/selectComponents/AllocateDates';
import { TooltipText } from '../../../components/inputComponents/TooltipText';
import { languageCodes } from '../../../constants/lists';
import LocalizedQuillInput from '../../../common/ReactQuill/LocalizedQuillInput';
import { FileUpload } from '../../../components/inputComponents/FileUpload/FileUpload.component';
import { appendPublicDomainName } from '../../../components/inputComponents/FileUpload/fileUploadHelpers';
import { getIn, useFormikContext } from 'formik';
import {
  editPromotionFormikState,
  PromoMechanic,
  PromoVariation,
} from '../../../types/componentTypes/editPromotion';
import { SelectLanguage } from '../../../components/assignPromotionFlowComponent/LanguageSelector';
import { EditPromotionGeneralInfoFormProps } from '../../../types/componentTypes/editPromotion';
import TextFieldWrapper from '../../../common/TextFieldWrapper/TextFieldWrapper';
import { defaultNotifState } from '../../../constants/currency-constants';
import { Notification } from '../../../common/Notification/Notification';

import './EditPromotionGeneralInfoForm.css';
import { useCountries } from '../../../hooks/useCountries';
import { toggleOptions, tooltipsText } from '../../../constants/promotion-constants';
import { EnableDoubleWinToggle } from '../../../components/inputComponents/EnableDoubleWinToggle';

const EditPromotionGeneralInfoForm = memo(
  ({
    campaignData,
    promotionVariation,
    promotionMechanic,
    emailTemplates,
    updateOnCountry,
    handleTabChange,
    updatePromoSubMechanic,
    hasPrizes,
    config,
  }: EditPromotionGeneralInfoFormProps) => {
    const [selectedLanguage, setSelectedLanguage] = useState<
      string | undefined
    >();
    const formik = useFormikContext<editPromotionFormikState>();
    const [country, setCountry] = useState<string | undefined>();
    const [notificationState, setNotificationState] =
      useState(defaultNotifState);
    const [changesApplied, setChangesApplied] = useState(false);
    const [initialCountry, setInitialCountry] = useState<string | undefined>();
    const [initialLanguage, setInitialLanguage] = useState<string | undefined>();

    const { displayMarketOptions } = useCountries();

    const redemptionApps = [
        {value: '', label: 'None'},
        {value: 'onexp-redemption-app', label: 'OneXP Redemption App'},
    ]

    useEffect(() => {
      const initialCountryVal = formik.initialValues?.configurationParameters?.country;
      const initialLanguageVal = formik.initialValues?.configurationParameters?.language;

      if (initialCountryVal && initialLanguageVal) {
        setInitialCountry(initialCountryVal);
        setInitialLanguage(initialLanguageVal);
      }
    }, [formik.initialValues]);

    useEffect(() => {
      const langSelected =
        languageCodes[formik.values.configurationParameters.language];
      setSelectedLanguage(langSelected);
    }, [formik.values.configurationParameters.language]);

    const {
      values: {
        configurationParameters: { country: selectedCountry, language },
      },
      setFieldValue,
    } = formik;

    useEffect(() => {
      if (selectedCountry === initialCountry && language === '') {
        setFieldValue(
          'configurationParameters.language',
          initialLanguage || ''
        );
      }

      if (
        !campaignData.promotion_market.includes(selectedCountry) &&
        selectedCountry !== ''
      ) {
        setCountry(selectedCountry);
      }
    }, [
      campaignData.promotion_market,
      selectedCountry,
      language,
      initialCountry,
      initialLanguage,
      setFieldValue
    ]);
    useEffect(() => {
      const isObject = (value: any) =>
        typeof value === 'object' && value !== null && !Array.isArray(value);

      const normalizeDateString = (value: any) => {
        if (
          typeof value === 'string' &&
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?([+-]\d{2}:\d{2})?$/.test(value)
        ) {
          const date = new Date(value);
          return date.toISOString();
        }
        return value;
      };

      const collectDifferences = (current: any, initial: any, path = '', differences: any = {}) => {
        const keys = new Set([...Object.keys(current || {}), ...Object.keys(initial || {})]);

        keys.forEach((key) => {
          const fullPath = path ? `${path}.${key}` : key;
          const val1 = current?.[key];
          const val2 = initial?.[key];

          if (isObject(val1) && isObject(val2)) {
            collectDifferences(val1, val2, fullPath, differences);
          } else {
            const normalizedVal1 = normalizeDateString(val1);
            const normalizedVal2 = normalizeDateString(val2);

            if (JSON.stringify(normalizedVal1) !== JSON.stringify(normalizedVal2)) {
              differences[fullPath] = {
                current: normalizedVal1,
                initial: normalizedVal2,
              };
            }
          }
        });

        return differences;
      };

      const differences = collectDifferences(formik.values, formik.initialValues);
      const hasChanges = Object.keys(differences).length > 0;

      setChangesApplied(hasChanges);
      handleTabChange(hasChanges);
    }, [formik.values, formik.initialValues, handleTabChange]);

    const getImgUrl = (fileName) => {
      return appendPublicDomainName({
        path: `configurationImages/${getIn(
          formik.values,
          'configurationId'
        )}/${fileName}`,
      });
    };

    const defineLoyaltyMechanic = () => {

      if(!formik.values.tempParams.autoRedeemPrizeLambda){
        return toggleOptions.collectAndGet.standard;
      }

      if(formik?.values?.flow?.autoRedeemCnG?.secrets){
        return toggleOptions.autoCollectAndGet.withCodes;
      }
      return toggleOptions.autoCollectAndGet.noCodes;
    }

    const handleFile = (action: 'upload' | 'del', file?: string) => {
      switch (action) {
        case 'upload':
          formik.setFieldValue(
            'configurationParameters.additionalInformation.imgUrl',
            file
          );
          config.configurationParameters.additionalInformation.imgUrl = file;
          formik.setFieldValue('tempParams.imgUrl', '');
          return;
        case 'del':
          formik.setFieldValue('tempParams.imgUrl', '');
          config.configurationParameters.additionalInformation.imgUrl = '';
          formik.setFieldValue(
            'configurationParameters.additionalInformation.imgUrl',
            ''
          );
          return;
      }
    }

    return (
      <Box className='generalInfoForm'>
        <Typography variant='h3'>General Info</Typography>
        <Notification
          notificationState={notificationState}
          setNotificationState={setNotificationState}
        />
        <Grid container columnSpacing={4}>
          <Grid item xs={5} className='disabled'>
            <Typography variant='body1'>Linked Campaign</Typography>
            <InputComponent
              formik={formik}
              inputValue={getIn(formik.values, 'promotionId')}
              formikLabel={'promotionId'}
              title=''
              titleVariant='caption'
              label=''
              disabled={true}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={4}>
          <Grid item xs={5} className='disabled'>
            <Typography variant='body1'>Promotion Mechanic</Typography>
            <TextFieldWrapper
              className='input-text'
              fullWidth
              disabled={true}
              id='promo-mechanic-outlined-basic'
              value={promotionMechanic}
              variant='outlined'
            />
          </Grid>
          <Grid item xs={5} className='disabled'>
            <Grid container>
              <Grid
                container
                direction='row'
                justifyContent='space-between'
                sx={{ height: '23px' }}
              >
                <Grid item>
                  <Typography variant='body1'>Variation</Typography>
                </Grid>
                {promotionMechanic === PromoMechanic.InstantWin &&
                  promotionVariation === PromoVariation.InstantWinAW && (
                    <Grid item>
                      <EnableDoubleWinToggle
                        checked={!!getIn(formik.values, 'flow.instantWin.params.doubleWin')}
                        onChange={(e) => {
                          if (!e.target.checked) {
                            formik.setFieldValue('flow.instantWin.params.doubleWin', false);
                          }
                        }}
                        disabled={!getIn(formik.initialValues, 'flow.instantWin.params.doubleWin')}
                        labelSx={{ ml: 2 }}
                      />
                    </Grid>
                  )}
              </Grid>

              {(promotionMechanic === PromoMechanic.CollectAndGet ||
                promotionMechanic === PromoMechanic.AutoRedeemCnG) && (
                  <Grid item xs={12}>
                    <TextFieldWrapper
                      className='input-text'
                      fullWidth
                      disabled={true}
                      id='auto-redeem-outlined-basic'
                      value={defineLoyaltyMechanic()}
                      variant='outlined'
                    />
                  </Grid>
                )}

              {promotionMechanic === 'Instant Win' &&
                promotionVariation !== 'Instant Win' && (
                  <Grid item xs={12}>
                    <TextFieldWrapper
                      className='input-text'
                      fullWidth
                      disabled={true}
                      id='promo-variation-outlined-basic'
                      value={promotionVariation}
                      variant='outlined'
                    />
                  </Grid>
                )}

              {promotionMechanic === 'Prize Draw (Lottery)' && (
                <Grid item xs={12}>
                  <TextFieldWrapper
                    className='input-text'
                    fullWidth
                    disabled={true}
                    id='promo-variation-outlined-basic'
                    value={promotionVariation}
                    variant='outlined'
                  />
                </Grid>
              )}

              {promotionMechanic === 'Instant Win' &&
                promotionVariation === 'Instant Win' && (
                  <Grid item xs={12}>
                    <SelectOption
                      formik={formik}
                      formikValue={
                        'configurationParameters.configurationSubMechanic'
                      }
                      optionsList={[
                        PromoVariation.InstantWinAW,
                        PromoVariation.InstantWinWMGP,
                      ]}
                      inputLabel='Select Promotion Variation'
                      dataTestId='selectInstantWinVariation'
                      customAction={updatePromoSubMechanic}
                      customParam={{ formik: formik }}
                    />
                  </Grid>
                )}
            </Grid>
          </Grid>
        </Grid>
        <Grid container columnSpacing={4}>
          <Grid item xs={5}>
            <TooltipText
              dataTestId='market-tooltip-general-info-tab'
              text='Market'
              textVariant='body1'
              tooltip={tooltipsText.market}
              iconSize='medium'
              styleName='tooltip'
            />
            <SelectOption
              formik={formik}
              formikValue={'configurationParameters.country'}
              optionsList={Array.from(
                new Set([country, ...displayMarketOptions(campaignData.promotion_market, true)])
              )?.filter(val => val)}
              inputLabel=''
              dataTestId='selectMarket'
              customAction={updateOnCountry}
              customParam={{ formik: formik }}
            />
          </Grid>
          <Grid item xs={5}>
            <TooltipText
              dataTestId='default-language-general-info-tab'
              text='Default Language'
              textVariant='body1'
              tooltip={tooltipsText.language}
              iconSize='medium'
              styleName='tooltip'
            />
            <SelectLanguage
              formik={formik}
              countryValue={formik.values.configurationParameters.country}
              formikLanguageField={'configurationParameters.language'}
              selectedLanguage={selectedLanguage}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={4}>
          <Grid item xs={5}>
            <Typography variant='body1'>Promotion Name</Typography>
            <InputComponent
              formik={formik}
              inputValue={getIn(
                formik.values,
                'configurationParameters.additionalInformation.name'
              )}
              formikLabel={'configurationParameters.additionalInformation.name'}
              title=''
              titleVariant='caption'
              label=''
              dataTestId='promotionName'
            />
          </Grid>
          <Grid item xs={5}>
            <TooltipText
              dataTestId='promotion-image-general-info-tab'
              text={'Promotion Image (Optional)'}
              textVariant='body1'
              tooltip='Allowed file formats: PNG, JPG, SVG. Change image: Delete the old one and add new.'
              iconSize='medium'
              styleName='tooltip'
            />
            <FileUpload
              cachedFiles={
                getIn(formik.values, 'tempParams.imgUrl')
                  ? [{ name: getIn(formik.values, 'tempParams.imgUrl') }]
                  : []
              }
              placeholder={'No file chosen'}
              bucket='public'
              filePath={`configurationImages/${getIn(
                formik.values,
                'configurationId'
              )}`}
              uploadCallback={(files: Array<String>) => {
                const file = files.length ? getImgUrl(files[0]) : '';
                handleFile('upload', file)
              }}
              deleteCallback={() => handleFile('del')}
              customAllowedFileTypes={['image/png', 'image/jpeg', 'image/svg+xml']}
              setNotificationState={setNotificationState}
              canDelete={true}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={4}>
          <Grid item xs={5}>
            <Typography variant='body1'>
              Short Description (Optional)
            </Typography>
            <LocalizedQuillInput
              className={'editPromotionDescriptionQuills'}
              formik={formik}
              name='configurationParameters.additionalInformation.shortDescription'
              dataTestId='short-description-general-info-tab'
            />
          </Grid>
          <Grid item xs={5}>
            <Typography variant='body1'>Long Description (Optional)</Typography>
            <LocalizedQuillInput
              className={'editPromotionDescriptionQuills'}
              formik={formik}
              name='configurationParameters.additionalInformation.description'
              dataTestId='long-description-general-info-tab'
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={4}>
          <Grid item xs={10}>
            <AllocateDates
              datetime={true}
              formikStartDate={`configurationParameters.configurationStartUtc`}
              formikEndDate={`configurationParameters.configurationEndUtc`}
              maxEndDate={campaignData?.promotion_end_utc}
              isEdit={true}
              formikTimezoneValue={`configurationParameters.configurationDatesTimezone`}
              useTitles={true}
              containerSpacing={4}
              startEndDateColumns={3}
              timeZoneColumns={6}
              disabled={hasPrizes}
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={4}>
          <Grid item xs={5}>
            <TooltipText
              dataTestId='emial-template-general-info-tab'
              text='Email Template (Optional)'
              textVariant='body1'
              tooltip={tooltipsText.email}
              iconSize='medium'
              styleName='tooltip'
            />
            <SelectOption
              formik={formik}
              formikValue={'configurationParameters.emailTemplateId'}
              optionsList={[
                { value: '', label: 'None' },
                ...Array.from(
                  new Set([
                    ...emailTemplates,
                    ...(formik.values.configurationParameters.emailTemplateId
                      ? [formik.values.configurationParameters.emailTemplateId]
                      : []),
                  ])
                ),
              ]}
              inputLabel=''
              dataTestId='emailTemplates'
            />
          </Grid>
          <Grid item xs={5}>
            <TooltipText
              dataTestId='ajo-template-general-info-tab'
              text='AJO Template Name (Optional)'
              textVariant='body1'
              tooltip={tooltipsText.ajoEmail}
              iconSize='medium'
              styleName='tooltip'
            />
            <InputComponent
              formik={formik}
              inputValue={getIn(
                formik.values,
                'configurationParameters.ajoEmailTemplate'
              )}
              formikLabel={'configurationParameters.ajoEmailTemplate'}
              errorPath={['configurationParameters.ajoEmailTemplate']}
              title=''
              titleVariant='caption'
              label=''
              dataTestId='ajoTemplate'
            />
          </Grid>
          <Grid item xs={5}>
            <TooltipText
              text='Redemption App (Optional)'
              textVariant='body1'
              tooltip={tooltipsText.redemptionApp}
              iconSize='medium'
              styleName='tooltip'
            />
            <SelectOption
              formik={formik}
              formikValue={'configurationParameters.redemptionApp'}
              optionsList={redemptionApps}
              inputLabel=''
              dataTestId='redemption-app-dropdown-general-info-tab'
            />
          </Grid>
          <Grid item xs={5}>
            <TooltipText
              dataTestId='ajo-rejection-email-template-tooltip'
              text='AJO Template For Rejected Participation (Optional)'
              textVariant='body1'
              tooltip={tooltipsText.ajoEmail}
              iconSize='medium'
              styleName='tooltip'
            />
            <InputComponent
              formik={formik}
              inputValue={getIn(
                formik.values,
                'configurationParameters.ajoRejectionEmailTemplate'
              )}
              formikLabel={'configurationParameters.ajoRejectionEmailTemplate'}
              title=''
              titleVariant='caption'
              label=''
              dataTestId='ajoRejectionEmailTemplate'
            />
          </Grid>
        </Grid>
        <Grid container columnSpacing={4}>
          <Grid item xs={10} textAlign='right'>
            <Button
              variant='contained'
              color='primary'
              type='submit'
              className='submitBtn'
            >
              SAVE
            </Button>
            {changesApplied && (
              <Button
                variant='outlined'
                color='primary'
                onClick={() => formik.resetForm({ values: formik.initialValues })}
                className='discardBtn'
              >
                DISCARD
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  }
);

export { EditPromotionGeneralInfoForm };
